import React, { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import oktaInstance from "../../auth/oktaConfig";
import Button from "./Button";
import {
  Menu,
  MenuItem,
  MenuTrigger,
  MenuPopover,
  MenuList,
  Button as FluentButton,
  makeStyles,
} from "@fluentui/react-components";
import { ChevronDown12Regular } from "@fluentui/react-icons";

interface LoginButtonInterface {
  isInitialized: boolean;
  setIsInitialized: (isInitialized: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  userChanged: (authToken: string, ID: string, oktaIdToken: string, name: string, identityProvider: string) => void;
}

const useStyles = makeStyles({
  dropDownButton: {
    borderRadius: "6px",
    border: "0.25pt solid #C4E1E0",
    backgroundColor: "#ffffff",
    color: "#001C71",
    fontSize: "11px",
    display: "block",
    "&:hover": {
      border: "0.25pt solid #7FBDBA",
      backgroundColor: "#ffffff",
      color: "#001C71",
    },
    "&:disabled": {
      border: "0.25pt solid #E0E0E0",
      backgroundColor: "#F5F5F5",
      color: "#A0A0A0",
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
});

const LoginButton: React.FC<LoginButtonInterface> = ({
  isInitialized,
  setIsInitialized,
  setIsLoading,
  userChanged,
}) => {
  const { instance } = useMsal();
  window["authInstance"] = instance;
  window["oktaInstance"] = oktaInstance;
  
  const styles = useStyles();

  const handleLogin = async () => {
    setIsLoading(true);
    const scopes = ["openid", "profile", "email"];
    const loginCompleted = (response: any) => {
      let account = response.account;
      userChanged(response.idToken, response.idToken, account.username, account.name, "Entra");
      instance.setActiveAccount(account);
      console.log("Login successful.");
      try {
        localStorage.setItem("loginMethod", "microsoft");
      } catch (error) {
        console.error("LocalStorage is not available:", error);
      }
    };
    try {
      try {
        await instance.ssoSilent({ scopes }).then(loginCompleted);
      } catch {
        await instance.loginPopup().then(loginCompleted);
      }
    } catch (error) {
      console.error("Login failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginWithOkta = async () => {
    setIsLoading(true);

    try {
      const tokenResponse = await oktaInstance.token.getWithPopup({
        responseType: ["token", "id_token"],
        scopes: ["openid", "profile", "email"],
      });

      oktaInstance.tokenManager.add("idToken", tokenResponse.tokens.idToken);
      oktaInstance.tokenManager.add("accessToken", tokenResponse.tokens.accessToken);

      const accessToken = tokenResponse.tokens.accessToken?.accessToken;
      const idToken = tokenResponse.tokens.idToken?.idToken;

      const userEmail: string = tokenResponse.tokens.idToken?.claims.email || "";
      const userName: string = tokenResponse.tokens.idToken?.claims.name || "";
      userChanged(idToken, idToken, userEmail, userName, "Okta");

      console.log("Okta Login successful.");
      try {
        localStorage.setItem("loginMethod", "okta");
      } catch (error) {
        console.error("LocalStorage is not available:", error);
      }
    } catch (error) {
      console.error("Okta Login failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginWithLastOption = async () => {
    const loginMethod = localStorage.getItem("loginMethod");
    if (loginMethod === "okta") {
      await handleLoginWithOkta();
    } else {
      await handleLogin();
    }
  };

  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      setTimeout(() => {
        setIsLoading(true);
        const loginMethod = localStorage.getItem("loginMethod");
        if (loginMethod === "okta") {
          setTimeout(handleLoginWithOkta);
          console.log("Automatic login with Okta");
        } else {
          setTimeout(handleLogin);
          console.log("Automatic login with Microsoft");
        }
      });
    }
  });

  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <Button onClick={handleLoginWithLastOption} label="Sign in" />;
      <Menu>
        <MenuTrigger>
          <FluentButton
            icon={<ChevronDown12Regular />}
            size="small"
            className={styles.dropDownButton}
            style={{ padding: "1px", margin: "0px", marginLeft: "-8px" }}
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList style={{ marginTop: "8px" }}>
            <MenuItem onClick={handleLogin}>with Microsoft</MenuItem>
            <MenuItem onClick={handleLoginWithOkta}>with Okta</MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </span>
  );
};

export default LoginButton;
